import React from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { PartnerProviderLog } from "@/types/partner-provider-log";
import { formatDateTime } from "@/utils/dateUtils";
import {
  getLatestFailedPartnerProviderLog,
  getLatestPartnerProviderLog,
  getPartnerProviderState,
} from "../utils/partnerProviderLogs";

interface PartnerProviderStatusCardProps {
  logs: PartnerProviderLog[];
  lastSyncAt?: Date | string;
  isLoading: boolean;
}

const providerStateBadgeVariant = {
  healthy: "verified",
  error: "new_destructive",
  apiError: "new_destructive",
  syncing: "new_default",
  triggered: "new_secondary",
  unknown: "secondary",
} as const;

export const PartnerProviderStatusCard: React.FC<
  PartnerProviderStatusCardProps
> = ({ logs, lastSyncAt, isLoading }) => {
  const { t } = useTranslation();

  const latestLog = getLatestPartnerProviderLog(logs);
  const failedLog = getLatestFailedPartnerProviderLog(logs);
  const providerState = getPartnerProviderState(latestLog);
  const stateLabelKey = `partners.admin.statusValues.${providerState}`;

  return (
    <div className="flex flex-col gap-3 rounded-lg border p-4">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-medium">
          {t("partners.providerStatus")}
        </span>
        <Badge variant={providerStateBadgeVariant[providerState]}>
          {isLoading && logs.length === 0
            ? t("partners.loadingLogs")
            : t(stateLabelKey)}
        </Badge>
      </div>

      {lastSyncAt && (
        <p className="text-sm text-muted-foreground">
          {t("partners.lastSyncAt")}: {formatDateTime(String(lastSyncAt))}
        </p>
      )}

      {latestLog && (
        <p className="text-sm text-muted-foreground">
          {t("partners.latestEvent")}:{" "}
          {t(`partners.admin.eventTypeValues.${latestLog.eventType}`, {
            defaultValue: latestLog.eventType,
          })}
        </p>
      )}

      {failedLog?.message && (
        <p className="text-sm text-destructive">
          {t("partners.latestError")}: {failedLog.message}
        </p>
      )}
    </div>
  );
};
